import type { Text } from '../Text';
import type { TextExtension } from '../types';

/**
 * Extension interface providing regex-based extraction methods.
 */
export interface ExtractionExtension extends Text {
  /**
   * Extracts the first match or capture group from the string using the provided pattern.
   * If the pattern has a capture group, returns the first capture group.
   * Otherwise, returns the full match.
   */
  extractSubstring(pattern: RegExp): string;

  /**
   * Extracts all matches or capture groups from the string using the provided pattern.
   * If the pattern has capture groups, returns an array of the first capture group from each match.
   * Otherwise, returns an array of full matches.
   */
  extractSubstrings(pattern: RegExp): string[];

  /**
   * Extracts all capture groups from the first match of the provided pattern.
   * Returns an empty array if the pattern doesn't match. Unmatched optional
   * groups are returned as empty strings.
   */
  extractGroups(pattern: RegExp): string[];
}

/**
 * Creates an extension that adds regex-based extraction methods to Text.
 *
 * @example
 * const t = $$$('user:123', withExtraction());
 * t.extractSubstring(/user:(\d+)/); // '123'
 *
 * @example
 * const t = $$$('a1 b2 c3', withExtraction());
 * t.extractSubstrings(/[a-z](\d)/g); // ['1', '2', '3']
 */
export function withExtraction(): TextExtension<ExtractionExtension> {
  return (text) => {
    const $$ = text.$$;

    return {
      extractSubstring(pattern: RegExp): string {
        let p = pattern;
        if (p.global) {
          p = new RegExp(p, p.flags.replace('g', ''));
        }

        const match = $$.match(p);
        return match ? (match.length > 1 ? match[1]! : match[0]) : '';
      },

      extractSubstrings(pattern: RegExp): string[] {
        let p = pattern;
        const arr: string[] = [];

        if (!p.global) {
          p = new RegExp(p, p.flags + 'g');
        }

        p.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = p.exec($$))) {
          arr.push(match.length > 1 ? match[1]! : match[0]);
        }

        return arr;
      },

      extractGroups(pattern: RegExp): string[] {
        let p = pattern;
        if (p.global) {
          p = new RegExp(p, p.flags.replace('g', ''));
        }

        const match = $$.match(p);
        return match
          ? match.slice(1).map((group: string | undefined) => group ?? '')
          : [];
      },
    };
  };
}
